/**
 * readingScale - how large a reviewer wants document text, everywhere.
 *
 * GLOBAL PER USER, deliberately. A comfortable reading size is a property of
 * the person and the screen they are sitting at, not of one contract: someone
 * who needs 120% needs it on every document they open, and a per-document or
 * per-review setting would make them set it again on each one. So there is a
 * single value, it follows the user between sessions and devices, and the only
 * thing a document contributes is what it looks like at that size.
 *
 * WHERE IT LIVES. The same remote store the pane layouts use —
 * `/user/ui-layouts`, through `layoutTransport` - under its own scope,
 * `prefs:reading`. That endpoint is a per-user JSON bucket keyed by
 * `kind`/`name`, which is exactly the shape this needs; the alternative,
 * `/user/edit_preferences`, is a typed `PreferenceSettings` object the backend
 * defines, so a key it does not know about is one it may quietly drop.
 *
 * READS ARE SYNCHRONOUS, because the size has to be right on the first frame
 * or the document visibly re-flows after it renders. `localStorage` answers
 * immediately and `hydrate` reconciles with the server afterwards - the same
 * split, and for the same reason, as the layout store.
 */

import {
  readRemoteLayout,
  writeRemoteLayout,
  RemoteLayoutScope,
} from '../layout/layoutTransport';

/** The scope this preference occupies in the per-user bucket. */
const SCOPE: RemoteLayoutScope = { kind: 'prefs', name: 'reading' };

const CACHE_PREFIX = 'pons:prefs:reading';

/**
 * The ladder, in multiples of the base size.
 *
 * Discrete steps rather than free zoom: every stop is a size someone chose to
 * make legible, and there is no way to land on 103% and wonder why the text
 * looks slightly wrong.
 */
export const READING_SCALES = [0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2] as const;

/**
 * 1 is the DEFAULT, and the base it multiplies is 15px - one step below the
 * browser's 16px, which is what the document rendered at before. On a screen
 * held further away than a sheet of paper, 16 read slightly large.
 */
export const READING_BASE_PX = 15;
export const DEFAULT_READING_SCALE = 1;

const clampToLadder = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_READING_SCALE;
  // Snap to the nearest rung: a value from an older build (or a hand-edited
  // cache) still lands somewhere the +/- buttons can move from.
  return READING_SCALES.reduce(
    (best, step) => (Math.abs(step - n) < Math.abs(best - n) ? step : best),
    DEFAULT_READING_SCALE as number,
  );
};

const cacheKey = (userId: string) => `${CACHE_PREFIX}:${userId || 'anon'}`;

/** The size to render at right now - cache first, so the first frame is right. */
export const loadReadingScale = (userId: string): number => {
  try {
    const raw = window.localStorage.getItem(cacheKey(userId));
    if (raw == null) return DEFAULT_READING_SCALE;
    return clampToLadder(JSON.parse(raw)?.scale);
  } catch {
    return DEFAULT_READING_SCALE;
  }
};

/** Write locally at once; the server catches up on a debounce. */
export const saveReadingScale = (userId: string, scale: number): void => {
  const value = clampToLadder(scale);
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify({ scale: value }));
  } catch {
    /* out of quota - the session still has it in memory */
  }
  queueRemoteWrite(value);
};

/* A burst of +/- presses is one intent, so it is one request. */
const REMOTE_DEBOUNCE_MS = 800;
let pending: number | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

const queueRemoteWrite = (scale: number) => {
  pending = scale;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    const value = pending;
    pending = null;
    if (value == null) return;
    void writeRemoteLayout(SCOPE, { scale: value });
  }, REMOTE_DEBOUNCE_MS);
};

/**
 * Ask the server what this user chose, and adopt it.
 *
 * Returns null when there is nothing stored or the read fails, in which case
 * whatever the cache answered stands - a failed sync must never reset someone
 * to the default.
 */
export const hydrateReadingScale = async (userId: string): Promise<number | null> => {
  const result = await readRemoteLayout(SCOPE);
  if (!result.ok || !result.layout) return null;
  const scale = clampToLadder((result.layout as { scale?: unknown }).scale);
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify({ scale }));
  } catch {
    /* cache is an optimisation */
  }
  return scale;
};

/** The step above/below, or the same value at either end of the ladder. */
export const stepReadingScale = (scale: number, direction: 1 | -1): number => {
  const current = READING_SCALES.indexOf(clampToLadder(scale) as typeof READING_SCALES[number]);
  const next = current + direction;
  if (next < 0 || next >= READING_SCALES.length) return READING_SCALES[current];
  return READING_SCALES[next];
};

/** `110%` - what the control shows. */
export const readingScaleLabel = (scale: number): string =>
  `${Math.round(clampToLadder(scale) * 100)}%`;

/** The px value the document's root should use. */
export const readingScalePx = (scale: number): number =>
  Math.round(READING_BASE_PX * clampToLadder(scale) * 100) / 100;
